// Lo que la cabecera y la barra lateral del portal dicen sobre la encuesta de
// ofertas (rediseño, docs/PLAN-REDISENO-PORTAL.md §6.2 y §13): la hora de la
// planta, el indicador "Ofertas al día" y el contador de Viajes. Los enchufa
// el portal en el shell (shell/types.ts, HeaderStatus y NavBadges).
//
// Leen la encuesta unica (offer-alert/OffersFeed.tsx, PP-71), no piden nada
// por su cuenta. Cada uno lee adentro de su componente: una vuelta de la
// encuesta repinta estos tres, no el shell ni la seccion. La logica es
// logic/header.ts.
//
// El reloj usa la hora del navegador en la zona IANA de la planta (regla 9):
// la zona sale de la encuesta y la hora se pinta con Intl en esa zona, nunca
// en la del navegador. Un instante del servidor queda como propuesta
// (§9.2); los plazos ya se miden contra `deadlineAt`.
import { useEffect, useMemo, useState } from "react";
import { cn } from "../components/ui/cn.js";
import { offersStatus, offersStatusText, pendingCounts, pendingTripsLabel, plantClocks, type PlantClock } from "../logic/header.js";
import { offersInScope } from "../logic/offer-alert.js";
import { useOffersFeed } from "../offer-alert/index.js";
import type { Clock } from "../requests/clock.js";
import { SidebarCounter } from "../shell/Sidebar.js";
import type { ShellScope } from "../shell/types.js";

const MINUTE_MS = 60_000;
/**
 * Cada cuanto se mira si cambio el minuto. Mirar no repinta: el estado es el
 * numero de minuto y React no repinta si no cambio, asi que se repinta una vez
 * por minuto con a lo sumo 5 segundos de atraso (un tick de 60 s podia
 * mostrar la hora vieja casi un minuto entero).
 */
export const MINUTE_CHECK_MS = 5_000;

/** El minuto en curso (desde epoch); cambia una vez por minuto. */
export function useMinute(clock: Clock): number {
  const [minute, setMinute] = useState(() => Math.floor(clock.now() / MINUTE_MS));
  useEffect(() => {
    setMinute(Math.floor(clock.now() / MINUTE_MS));
    return clock.every(MINUTE_CHECK_MS, () => setMinute(Math.floor(clock.now() / MINUTE_MS)));
  }, [clock]);
  return minute;
}

/** Las ofertas de la encuesta en el alcance del filtro, o `undefined` mientras no hay datos. */
function useScopedOffers(scope: ShellScope) {
  const { state } = useOffersFeed();
  const items = state.estado === "listo" ? state.data.items : undefined;
  const scoped = useMemo(() => (items === undefined ? undefined : offersInScope(items, scope.sites, scope.siteId).scoped), [items, scope.sites, scope.siteId]);
  return { state, scoped };
}

/** Con "Todas" y una sola zona para varias plantas, la hora es "de las plantas"; si no, dice de cuales. */
function clockLabel(clock: PlantClock, single: boolean, filtered: boolean): string {
  if (single && !filtered && clock.siteNames.length > 1) return "Hora de las plantas";
  return `Hora en ${clock.siteNames.join(", ")}`;
}

/**
 * La hora de la planta. Con una planta filtrada, la de su zona; con "Todas" y
 * una sola zona, una hora; con zonas distintas, cada hora con los nombres de
 * sus plantas al lado. Sin la encuesta todavia no hay zona: no se pinta nada
 * en vez de la hora del navegador.
 */
export function PlantClockView({ scope, clock }: { scope: ShellScope; clock: Clock }): React.JSX.Element | null {
  const minute = useMinute(clock);
  const { state } = useOffersFeed();
  const sites = state.estado === "listo" ? state.data.sites : undefined;
  const allowed = useMemo(() => new Set(scope.sites.map((site) => site.siteId)), [scope.sites]);
  const clocks = useMemo(
    () => (sites === undefined ? [] : plantClocks(sites, allowed, scope.siteId, minute * MINUTE_MS)),
    [sites, allowed, scope.siteId, minute],
  );
  if (state.estado === "cargando") return <span aria-hidden="true" className="hueso h-4 w-12" />;
  if (clocks.length === 0) return null;
  const single = clocks.length === 1;
  return (
    <ul aria-label="Hora de la planta" className="m-0 flex list-none items-center gap-3 p-0">
      {clocks.map((plantClock) => (
        <li key={plantClock.timezone} title={plantClock.timezone} className="flex items-baseline gap-1.5 whitespace-nowrap">
          <span className="sr-only">{`${clockLabel(plantClock, single, scope.siteId !== null)}: `}</span>
          <span aria-hidden="true" className="text-micro font-medium uppercase tracking-eyebrow text-texto-debil">
            {single ? "Hora planta" : ""}
          </span>
          <span className="tabular text-sm font-semibold text-texto-2">{plantClock.time}</span>
          {!single && (
            <span aria-hidden="true" className="max-w-[140px] truncate text-eyebrow text-texto-suave">
              {plantClock.siteNames.join(", ")}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

const TONE = {
  "al-dia": { dot: "bg-ok", text: "text-ok-texto" },
  "por-responder": { dot: "bg-naranja", text: "text-naranja-texto" },
  "sin-actualizar": { dot: "bg-ambar", text: "text-ambar" },
} as const;

/**
 * "Ofertas al día": verde con la encuesta sana y nada por responder, naranja
 * con ofertas por responder del filtro, ambar sin conexion o con la ultima
 * vuelta fallida (no se puede afirmar que este al dia). Cargando es un hueso.
 * El texto dice el estado; el punto lo acompana.
 */
export function OffersIndicator({ scope, clock }: { scope: ShellScope; clock: Clock }): React.JSX.Element | null {
  useMinute(clock);
  const { state, scoped } = useScopedOffers(scope);
  const toRespond = scoped === undefined ? 0 : pendingCounts(scoped, clock.now()).respond;
  const status = offersStatus({ estado: state.estado, stale: state.estado === "listo" && state.stale }, toRespond, scope.online);
  if (status === null) return null;
  if (status.kind === "cargando") {
    return (
      <span aria-busy="true" className="flex items-center">
        <span role="status" className="sr-only">
          {offersStatusText(status)}
        </span>
        <span aria-hidden="true" className="hueso h-5 w-28 rounded-pill" />
      </span>
    );
  }
  const tone = TONE[status.kind];
  return (
    <p role="status" className="m-0 inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-pill border border-linea bg-velo px-2.5 text-xs font-medium">
      <span aria-hidden="true" className={cn("size-1.5 rounded-pill", tone.dot)} />
      <span className={tone.text}>{offersStatusText(status)}</span>
    </p>
  );
}

/** Reloj e indicador juntos, para el enchufe `headerStatus` del shell. */
export function HeaderStatusBar({ scope, clock }: { scope: ShellScope; clock: Clock }): React.JSX.Element {
  return (
    <>
      <PlantClockView scope={scope} clock={clock} />
      <OffersIndicator scope={scope} clock={clock} />
    </>
  );
}

/**
 * Contador de Viajes: ofertas por responder y aceptadas por asignar del
 * filtro, con el reloj corriendo (las mismas que cuenta el titulo de la
 * pestana). De cualquier dia: la encuesta trae las ofertas vivas de todos.
 * Sin datos todavia, sin contador: un numero inventado no ayuda.
 */
export function TripsCounter({ scope, clock }: { scope: ShellScope; clock: Clock }): React.JSX.Element | null {
  useMinute(clock);
  const { scoped } = useScopedOffers(scope);
  if (scoped === undefined) return null;
  const { respond, assign } = pendingCounts(scoped, clock.now());
  const count = respond + assign;
  return <SidebarCounter count={count} label={pendingTripsLabel(count)} />;
}
